import type { ReactNode } from "react"
import { useEffect, useState } from "react"

import { formatDistanceToNow } from "date-fns"
import {
  Database,
  Loader2,
  Maximize2,
  Pencil,
  RefreshCw,
  Save,
  Sparkles,
  Timer,
  User,
  Users,
} from "@/components/icons"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useDateLocale } from "@/hooks/use-date-locale"
import { cn } from "@/lib/utils"

type DashboardToolbarProps = {
  isRefreshing: boolean
  lastUpdated: Date | null
  hasDashboardSpec: boolean
  currentDashboardId: string | null
  canRefresh: boolean
  updateOnLoad: boolean
  dashboardTitle: string
  dashboardType: "personal" | "company"
  isDashboardAdmin: boolean
  canEdit: boolean
  hasSqlBindings?: boolean
  isFocused?: boolean
  actions?: ReactNode
  onTitleChange: (title: string) => void
  onTypeChange: (type: "personal" | "company") => void
  onUpdateOnLoadChange: (enabled: boolean) => void
  onRefresh: () => void
  onSave: () => void
  onEditWithAI?: () => void
}

export function DashboardToolbar({
  isRefreshing,
  lastUpdated,
  hasDashboardSpec,
  currentDashboardId,
  canRefresh,
  updateOnLoad,
  dashboardTitle,
  dashboardType,
  isDashboardAdmin,
  canEdit,
  hasSqlBindings,
  isFocused,
  actions,
  onTitleChange,
  onTypeChange,
  onUpdateOnLoadChange,
  onRefresh,
  onSave,
  onEditWithAI,
}: DashboardToolbarProps) {
  const { t } = useTranslation()
  const dateLocale = useDateLocale()
  const [relativeTime, setRelativeTime] = useState("")

  // Update relative time every 30 seconds
  useEffect(() => {
    if (!lastUpdated) return

    const update = () => {
      setRelativeTime(formatDistanceToNow(lastUpdated, { addSuffix: true, locale: dateLocale }))
    }

    update()
    const timer = setInterval(update, 30_000)
    return () => clearInterval(timer)
  }, [lastUpdated, dateLocale])

  if (!hasDashboardSpec) return null

  return (
    <div className="border-border bg-card flex flex-wrap items-center gap-2 border-b px-4 py-2">
      {currentDashboardId && (
        <div className="flex items-center gap-2">
          <Input
            className="h-7 w-full max-w-[180px] min-w-[100px] text-xs"
            disabled={!canEdit}
            value={dashboardTitle}
            onChange={(e) => onTitleChange(e.target.value)}
          />
          {isDashboardAdmin ? (
            <Select
              disabled={!canEdit}
              value={dashboardType}
              onValueChange={(v) => onTypeChange(v as "personal" | "company")}
            >
              <SelectTrigger className="h-7 w-full max-w-[130px] min-w-[90px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal">
                  <span className="flex items-center gap-2">
                    <User className="size-3.5" />
                    {t("dash.dashboard.save_type_personal")}
                  </span>
                </SelectItem>
                <SelectItem value="company">
                  <span className="flex items-center gap-2">
                    <Users className="size-3.5" />
                    {t("dash.dashboard.save_type_company")}
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="bg-muted/50 text-muted-foreground flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs">
                  {dashboardType === "company" ? (
                    <>
                      <Users className="size-3.5" />
                      <span>{t("dash.dashboard.save_type_company")}</span>
                    </>
                  ) : (
                    <>
                      <User className="size-3.5" />
                      <span>{t("dash.dashboard.save_type_personal")}</span>
                    </>
                  )}
                </span>
              </TooltipTrigger>
              <TooltipContent>{t("dash.dashboard.read_only_tooltip")}</TooltipContent>
            </Tooltip>
          )}
        </div>
      )}

      {lastUpdated && (
        <span className="text-muted-foreground text-xs">
          {t("dash.dashboard.last_updated", { time: relativeTime })}
        </span>
      )}

      {hasSqlBindings !== undefined && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge className="gap-1 text-[10px]" variant="outline">
              {hasSqlBindings ? (
                <>
                  <Database className="size-3" />
                  {t("dash.dashboard.sql_driven", { defaultValue: "SQL" })}
                </>
              ) : (
                <>
                  <Sparkles className="size-3" />
                  {t("dash.dashboard.ai_driven", { defaultValue: "AI" })}
                </>
              )}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            {hasSqlBindings
              ? t("dash.dashboard.sql_driven_tooltip", {
                  defaultValue: "Data refreshed via SQL queries",
                })
              : t("dash.dashboard.ai_driven_tooltip", { defaultValue: "Data refreshed via AI" })}
          </TooltipContent>
        </Tooltip>
      )}

      {/* Compact icon-only toolbar — text labels were eating the
          horizontal space and competing with the dashboard's own
          tile chrome. Each button now surfaces its label via the
          Tooltip on hover, matching Grafana's panel-toolbar feel.
          The refresh button still flips to its loader state during
          a refresh so the user gets unambiguous in-flight feedback. */}
      <div className="ml-auto flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={t("dash.dashboard.update_on_load")}
              aria-pressed={updateOnLoad}
              className={cn("size-8 p-0", updateOnLoad && "text-primary bg-primary/10")}
              disabled={!canEdit}
              size="icon"
              variant="ghost"
              onClick={() => onUpdateOnLoadChange(!updateOnLoad)}
            >
              <Timer className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("dash.dashboard.update_on_load")}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={
                isRefreshing ? t("dash.dashboard.refreshing") : t("dash.dashboard.refresh_data")
              }
              className="size-8 p-0"
              disabled={isRefreshing || !canRefresh}
              size="icon"
              variant="ghost"
              onClick={onRefresh}
            >
              {isRefreshing ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RefreshCw className="size-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isRefreshing ? t("dash.dashboard.refreshing") : t("dash.dashboard.refresh_data")}
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={t("dash.dashboard.save")}
              className="size-8 p-0"
              disabled={!canEdit}
              size="icon"
              variant="ghost"
              onClick={onSave}
            >
              <Save className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("dash.dashboard.save")}</TooltipContent>
        </Tooltip>

        {onEditWithAI && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={
                  isFocused ? t("dash.dashboard.edit_with_ai") : t("dash.dashboard.maximize_dash")
                }
                className="size-8 p-0"
                size="icon"
                variant="ghost"
                onClick={onEditWithAI}
              >
                {isFocused ? <Pencil className="size-4" /> : <Maximize2 className="size-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isFocused ? t("dash.dashboard.edit_with_ai") : t("dash.dashboard.maximize_dash")}
            </TooltipContent>
          </Tooltip>
        )}
        {actions}
      </div>
    </div>
  )
}
